import React, { useState, useRef, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../components/ui/Button";
import { SuccessMessage } from "../components/ui/SuccessMessage";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { AuthService } from "../services/auth.service";
import {
  ClaimsService,
  type PaginatedResponse,
  type SummaryData,
} from "../services/claims.service";
import { ClaimsTable, getColumnHeaders, getColumnIdFromHeader } from "../components/ClaimsTable";
import { TableFilter } from "../components/ClaimsTable/TableFilter";
import { ProfileService } from "../services/profile.service";
import MainLayout from "../components/layouts/MainLayout";
import { type VisibilityState } from "@tanstack/react-table";

export const ClaimsPage: React.FC = () => {
  const navigate = useNavigate();
  const user = AuthService.getCurrentUser();
  const userProfile = user ? ProfileService.getUserProfileFromStorage() : null;
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pageSize] = useState(99999);
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [showScrollHint, setShowScrollHint] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const COLUMN_VISIBILITY_KEY = "claimsTableColumnVisibility";
  const SCROLL_HINT_KEY = "claimsTableScrollHintDismissed";

  // Fetch claims data with auto-refresh
  const {
    data: paginatedData,
    isLoading: isLoadingClaims,
    refetch: refetchClaims,
  } = useQuery<PaginatedResponse<SummaryData>>({
    queryKey: ["claims-summary"],
    queryFn: () => ClaimsService.getAllSummary(0, pageSize),
    refetchInterval: user?.mfaRequired === true ? 30000 : false, // Refetch every 30 seconds when mfaRequired is true
    enabled: user?.mfaRequired === true,
  });

  const claimsData = paginatedData?.content || [];

  // Load column visibility from localStorage on mount
  useEffect(() => {
    const savedVisibility = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    if (savedVisibility) {
      try {
        setColumnVisibility(JSON.parse(savedVisibility));
      } catch (e) {
        console.error("Failed to parse saved column visibility", e);
      }
    }
  }, [COLUMN_VISIBILITY_KEY]);

  // Save column visibility to localStorage whenever it changes
  useEffect(() => {
    if (Object.keys(columnVisibility).length > 0) {
      localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify(columnVisibility));
    }
  }, [columnVisibility, COLUMN_VISIBILITY_KEY]);

  // Show scroll hint if not dismissed and table has data
  useEffect(() => {
    const dismissed = localStorage.getItem(SCROLL_HINT_KEY);
    if (!dismissed && claimsData.length > 0) {
      setShowScrollHint(true);
      return;
    }
  }, [claimsData.length]);

  // Hide hint on horizontal scroll
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (container.scrollLeft > 0 && showScrollHint) {
        setShowScrollHint(false);
        localStorage.setItem(SCROLL_HINT_KEY, "true");
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [showScrollHint]);

  const dismissScrollHint = () => {
    setShowScrollHint(false);
    localStorage.setItem(SCROLL_HINT_KEY, "true");
  };

  // Get column headers from data
  const columnHeaders = useMemo(() => getColumnHeaders(claimsData), [claimsData]);

  // Convert columnVisibility from columnId keys to header keys for the selector
  const headerVisibility = useMemo(() => {
    const result: Record<string, boolean> = {};
    columnHeaders.forEach((header) => {
      const columnId = getColumnIdFromHeader(header, claimsData);
      result[header] = columnVisibility[columnId] !== false;
    });
    return result;
  }, [columnHeaders, columnVisibility, claimsData]);

  const handleClearFilters = () => {
    setGlobalFilter("");
  };

  const handleColumnVisibilityChange = (header: string, isVisible: boolean) => {
    const columnId = getColumnIdFromHeader(header, claimsData);
    setColumnVisibility((prev) => ({
      ...prev,
      [columnId]: isVisible,
    }));
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.match(/\.(xlsx|xls|pdf)$/i)) {
      setUploadError("Please select an Excel file (.xlsx, .xls) or PDF file (.pdf)");
      return;
    }

    setIsUploading(true);
    setUploadError(null);
    setUploadSuccess(false);

    try {
      await ClaimsService.uploadClaimsFile(file);
      setUploadSuccess(true);

      // Show loading indicator for 500ms after successful response
      setTimeout(async () => {
        setIsUploading(false);
        await refetchClaims();
      }, 500);
    } catch (error) {
      setIsUploading(false);
      setUploadError(error instanceof Error ? error.message : "Upload failed");
    }

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };
  if (!user) {
    navigate("/");
    return null;
  }

  return (
    <MainLayout>
      {/* Main Content */}
      <main className="flex-1 min-w-0 relative md:ml-0 ml-12">
        <div className="w-full max-w-none">
          {/* Welcome and Upload Section with #EFF0F2 background */}
          <div className="bg-[#EFF0F2]">
            <h2 className="text-3xl text-black font-bold pl-8 pt-8 pr-8 pb-0 sm:pt-14 sm:pl-14 sm:mb-6 uppercase">
              Welcome, {userProfile?.firstName || "User"}!
            </h2>

            <div className="p-8 sm:pl-14 sm:pr-14 sm:pb-8">
              {!(user?.mfaRequired === true) && (
                <p className="text-gray-600 mb-6 leading-relaxed max-w-3xl">
                  You can upload a file (Excel or PDF) only after verifying your phone number and
                  enabling MFA using an authenticator app. To set it up, please go to your{" "}
                  <Link
                    to="/profile"
                    className="text-link-blue underline hover:text-link-blue hover:opacity-80"
                  >
                    Profile page
                  </Link>
                  .
                </p>
              )}
              {!(user?.mfaRequired === false) && (
                <p className="text-gray-600 mb-6 leading-relaxed max-w-3xl">
                  You can upload a file (Excel or PDF) below. The claims data will be processed and
                  displayed in the table. The data refreshes automatically every 30 seconds.
                </p>
              )}

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".xlsx,.xls,.pdf"
                className="hidden"
              />

              <Button
                disabled={!(user?.mfaRequired === true) || isUploading}
                onClick={triggerFileInput}
                className="inline-flex items-center bg-black text-white hover:bg-gray-800 md:w-40"
              >
                {isUploading ? "Uploading..." : "Upload File"}
              </Button>

              {uploadError && (
                <ErrorMessage
                  message={uploadError}
                  onDismiss={() => setUploadError(null)}
                  className="mt-4"
                />
              )}

              {uploadSuccess && (
                <SuccessMessage
                  message="File uploaded successfully!"
                  onClose={() => setUploadSuccess(false)}
                  className="mt-4"
                />
              )}
            </div>
          </div>

          {user?.mfaRequired === true && (
            <div className="bg-[#EFF0F2]">
              <TableFilter
                globalFilter={globalFilter}
                onGlobalFilterChange={setGlobalFilter}
                onClearSearch={handleClearFilters}
                columnHeaders={columnHeaders}
                columnVisibility={headerVisibility}
                onColumnVisibilityChange={handleColumnVisibilityChange}
              />
            </div>
          )}

          {user?.mfaRequired === true && (
            <div className="bg-white pl-3 pt-3">
              <div ref={tableContainerRef} className="overflow-x-auto">
                <ClaimsTable
                  data={claimsData}
                  isLoading={isLoadingClaims}
                  globalFilter={globalFilter}
                  columnVisibility={columnVisibility}
                />
              </div>
              {showScrollHint && (
                <div className="hidden lg:flex items-center justify-center gap-2 py-3 text-sm text-gray-600">
                  <span>
                    Tip: Hold{" "}
                    <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 text-gray-700 font-medium mx-1">
                      Shift
                    </kbd>{" "}
                    + scroll to move horizontally
                  </span>
                  <button
                    onClick={dismissScrollHint}
                    className="text-gray-400 hover:text-gray-600 ml-2"
                    aria-label="Dismiss hint"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </MainLayout>
  );
};
